import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { InputConverter } from '../../../inputConverter';
import { VideoLogService } from '../../../services/video-log.service';
import { BaseComponent } from '../../base.component';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-video-log-controller-ng',
    templateUrl: './video-log-controller.component.html',
    styleUrls: ['../../sharedStyles/base.scss', './video-log-controller.component.scss']
})
export class VideoLogControllerComponent extends BaseComponent implements AfterViewInit, OnDestroy {
    @Input() public goToPreviousSegment = this.videoLogService.goToPreviousSegment.bind(this.videoLogService);
    @Input() public goToNextSegment = this.videoLogService.goToNextSegment.bind(this.videoLogService);
    @Input() public goToPreviousCounty = this.videoLogService.goToPreviousCounty.bind(this.videoLogService);
    @Input() public goToNextCounty = this.videoLogService.goToNextCounty.bind(this.videoLogService);
    @Input() public resumeVideo =  this.videoLogService.resumeVideo.bind(this.videoLogService);
    @Input() public stopVideo = this.videoLogService.stopVideo.bind(this.videoLogService);

    @Output() public readonly refreshSlider = this.videoLogService.refreshSlider;
    @Output() public readonly segmentEnd = this.videoLogService.segmentEnd;
    @Output() public readonly countyEnd = this.videoLogService.countyEnd;
    @Output() public readonly routeEnd = this.videoLogService.routeEnd;
    @Output() public readonly newOffset = this.videoLogService.newOffset;

    @Output() public readonly promptSegmentEnd = new EventEmitter<void>();
    @Output() public readonly promptCountyEnd = new EventEmitter<void>();
    @Output() public readonly promptSegmentBegin = new EventEmitter<void>();
    @Output() public readonly promptCountyBegin = new EventEmitter<void>();
    @Output() public readonly loadHistoricReport = new EventEmitter<void>();

    @ViewChild('menuToggle') private readonly menuToggleRef: ElementRef<HTMLDivElement>;
    @ViewChild('menuContainer') private readonly menuContainerRef: ElementRef<HTMLDivElement>;
    @ViewChild('yearSelector') private readonly yearSelectorRef: ElementRef<HTMLInputElement>;
    private readonly unsubscribe$ = new Subject<void>();

    @Input()
    @InputConverter()
    hideIntersectionDialogs: boolean = true;

    @Input()
    @InputConverter()
    hideDownloadBtn: boolean = true;

    @Input() set SegmentHighlight(value: boolean) {
        if (this.videoLogService.segHighlightEnabled !== value) {
            this.videoLogService.toggleSegmentHighlight();
        }
    }

    @Input()
    @InputConverter()
    allowZoomTypeChange: boolean = false;

    private _showMenu: boolean;
    set showMenu(value: boolean) {
        this._showMenu = value;
        this._showMenu ? this.onShow() : this.onHide();
    }
    get showMenu() {
        return this._showMenu;
    }
    private _toggleImageToggles = false;
    set toggleImageToggles(value: boolean) {
        this._toggleImageToggles = value;
    }
    get toggleImageToggles() {
        return this._toggleImageToggles;
    }

    @Input() get VideoLogService() {
        return this.videoLogService;
    }

    showThreeImages = false;
    topPxBtn = '25px';
    bottomPx = '';
    isPopUp = false;

    showLeftImage = true;
    showCenterImage = true;
    showRightImage = true;

    constructor(
        _vcf: ViewContainerRef,
        public readonly videoLogService: VideoLogService,
        private readonly cdr: ChangeDetectorRef
    ) {
        super(_vcf, true);
    }

    ngAfterViewInit() {
        this.videoLogService.newOffset.pipe(
            takeUntil(this.unsubscribe$),
            distinctUntilChanged()
        ).subscribe(_ => {
            this.cdr.detectChanges();
        });

        this.videoLogService.newPlayerState.pipe(
            takeUntil(this.unsubscribe$),
            distinctUntilChanged()
        ).subscribe(_ => {
            this.cdr.detectChanges();
        });

        this.videoLogService.segmentEnd.subscribe((position: string) => {
            if (position === 'last') {
                this.promptSegmentEnd.emit();
            } else if (position === 'first') {
                this.promptSegmentBegin.emit();
            }

        });
        this.videoLogService.countyEnd.subscribe((position: string) => {
            if (position === 'last') {
                this.promptCountyEnd.emit();
            } else if (position === 'first') {
                this.promptCountyBegin.emit();
            }
        });
        this.videoLogService.refreshSlider.subscribe((currentYearIndex: number) => {
            if (this.videoLogService.currentYear) {
                //assigning currentYear to itself to refresh value and fix issues where image's year was not matching slider's year
                this.videoLogService.currentYear = this.videoLogService.currentYear;
            } else {
                currentYearIndex = currentYearIndex > this.videoLogService.yearRange.yearIndexMax ? this.videoLogService.yearRange.yearIndexMax : currentYearIndex;
                this.videoLogService.currentYear = this.videoLogService.yearRange.years[currentYearIndex];
            }
            this.cdr.detectChanges();
        });

        this.videoLogService.panelsSwapped.subscribe((showThreeImages) => {
            this.showThreeImages = !showThreeImages;
            this.topPxBtn = !showThreeImages ? '' : '25px';
            this.bottomPx = !showThreeImages ? '0px' : '';
        });

        this.videoLogService.imageToggleButtonsReset.subscribe(() => {
            this.showCenterImage = true;
            this.showLeftImage = true;
            this.showRightImage = true;
        });
    }

    onYearSelect(yearIndex) {
        this.videoLogService.currentYear = this.videoLogService.yearRange.years[yearIndex];
        this.loadHistoricReport.emit();
        this.yearSelectorRef.nativeElement.blur();
    }

    private onShow() {
        window.addEventListener('mousedown', this.outsideClickHandler);
        window.addEventListener('dragstart', this.outsideClickHandler);
        this.menuToggleRef.nativeElement.addEventListener('mousedown', this.insideClickHandler);
        this.menuContainerRef.nativeElement.addEventListener('mousedown', this.insideClickHandler);
    }

    private onHide() {
        window.removeEventListener('mousedown', this.outsideClickHandler);
        window.removeEventListener('dragstart', this.outsideClickHandler);
        this.menuToggleRef.nativeElement.removeEventListener('mousedown', this.insideClickHandler);
        this.menuContainerRef.nativeElement.removeEventListener('mousedown', this.insideClickHandler);
    }

    toggleLeftImage() {
        this.showLeftImage = !this.showLeftImage;
        this.videoLogService.toggleImage('Left');
    }
    toggleCenterImage() {
        this.showCenterImage = !this.showCenterImage;
        this.videoLogService.toggleImage('Center');
    }
    toggleRightImage() {
        this.showRightImage = !this.showRightImage;
        this.videoLogService.toggleImage('Right');
    }

    private readonly outsideClickHandler = () => {
        this.showMenu = false;
    }

    private readonly insideClickHandler = (e: MouseEvent) => {
        e.stopPropagation();
    }

    ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    @HostListener('document:keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
        if (this.videoLogService.keyboardEnabled) {

            if (['Space', 'ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].includes(event.code)) {
                event.stopPropagation();
                event.preventDefault();
            }

            if (event.code === 'Space') {
                this.videoLogService.startOrStopVideo();
            }
            if (event.code === 'ArrowLeft') {

                if (event.shiftKey) {
                    if (this.videoLogService.canGoToFirstOffset) {
                        this.videoLogService.goToFirstOffset();
                    }
                } else {
                    if (this.videoLogService.canGoToPreviousOffset) {
                        this.videoLogService.goToPreviousOffset();
                    }
                }

            }
            if (event.code === 'ArrowRight') {

                if (event.shiftKey) {
                    if (this.videoLogService.canGoToLastOffset) {
                        this.videoLogService.goToLastOffset();
                    }
                } else {
                    if (this.videoLogService.canGoToNextOffset) {
                        this.videoLogService.goToNextOffset();
                    }
                }

            }
            if (event.code === 'ArrowDown') {

                if (event.shiftKey) {
                    this.videoLogService.playerSpeed = this.videoLogService.playerSetting.maxSpeed;
                } else {
                    if (this.videoLogService.playerSpeed + this.videoLogService.playerSetting.step <= this.videoLogService.playerSetting.maxSpeed) {
                        const rounding = 10;
                        this.videoLogService.playerSpeed = Math.round((this.videoLogService.playerSpeed + this.videoLogService.playerSetting.step) * rounding) / rounding;
                    }
                }

            }
            if (event.code === 'ArrowUp') {
                if (event.shiftKey) {
                    this.videoLogService.playerSpeed = this.videoLogService.playerSetting.minSpeed;
                } else {
                    if (this.videoLogService.playerSpeed - this.videoLogService.playerSetting.step >= this.videoLogService.playerSetting.minSpeed) {
                        const rounding = 10;
                        this.videoLogService.playerSpeed = Math.round((this.videoLogService.playerSpeed - this.videoLogService.playerSetting.step) * rounding) / rounding;
                    }
                }
            }
        }
    }

    @Input() public toggleButtonsForPopup = () => {
        this.isPopUp = !this.isPopUp;
    }
}
